import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, EnvelopeIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';

function EmailVerification() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setVerificationStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      setVerificationStatus('verifying');
      
      const response = await axiosInstance.post('/api/auth/verify-email', { token });
      
      setVerificationStatus('success');
      setMessage(response.data.message);
      setEmail(response.data.email);
    } catch (error) {
      console.log('Verification error response:', error.response?.data);
      console.log('Error message:', error.response?.data?.message);
      
      // Check if the error is due to user already being verified
      const errorMessage = error.response?.data?.message || '';
      console.log('Checking if already verified. Error message:', errorMessage);
      
      if (errorMessage.includes('already been verified') || 
          errorMessage.includes('already verified')) {
        console.log('User already verified - showing success state');
        setVerificationStatus('success'); // Show as success since they're already verified
        setMessage('Your email has already been verified! You can now log in to your account.');
        setEmail(error.response?.data?.email || '');
      } else {
        console.log('Not already verified - showing error state');
        setVerificationStatus('error');
        setMessage(errorMessage || 'Verification failed. Please try again.');
      }
    }
  };

  const getStatusContent = () => {
    switch (verificationStatus) {
      case 'verifying':
        return (
          <div className="text-center space-y-4">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <EnvelopeIcon className="h-8 w-8 text-blue-600 animate-pulse" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Verifying Your Email...
            </h1>
            <p className="text-gray-600 text-sm mb-4">
              Please wait while we verify your email address.
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center space-y-4">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Email Verified!
            </h1>
            <p className="text-gray-600 text-sm mb-4">
              {message}
            </p>
            {email && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                <h4 className="text-sm font-medium text-green-900 mb-2">✅ Verification Complete</h4>
                <p className="text-xs text-green-800">
                  Your email <strong>{email}</strong> has been successfully verified.
                  You can now log in to your CF Ride Share account.
                </p>
              </div>
            )}
          </div>
        );

      case 'error':
        return (
          <div className="text-center space-y-4">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Verification Failed
            </h1>
            <p className="text-gray-600 text-sm mb-4">
              {message}
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
              <h4 className="text-sm font-medium text-red-900 mb-2">What to do next?</h4>
              <ol className="text-xs text-red-800 space-y-1 list-decimal list-inside">
                <li>Check if you're using the correct verification link from your email</li>
                <li>Make sure the link hasn't expired (valid for 24 hours)</li>
                <li>Try registering again to receive a new verification email</li>
                <li>Contact support if the problem persists</li>
              </ol>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center min-h-screen py-12">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                {getStatusContent()}
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                <div className="text-center mt-8">
                  {verificationStatus === 'success' ? (
                    <Link
                      to="/login"
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                    >
                      Go to Login
                    </Link>
                  ) : (
                    <div className="space-y-3">
                      <Link
                        to="/register"
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                      >
                        Register Again
                      </Link>
                      <div>
                        <Link
                          to="/login"
                          className="text-sm text-indigo-600 hover:text-indigo-500"
                        >
                          Back to Login
                        </Link>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center mt-4">
                  <p className="text-xs text-gray-500">
                    Having trouble? Contact support at support@cfrideshare.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification; 